// Public oaiq-loader.js uses this prefix before the application bundle runs.
export const PENDING_OPT_OUT_PREFIX = 'jds.pending-ads-opt-out.';

/**
 * Why an account opt-out is still pending (saved locally, not yet acknowledged
 * by the payment service). The stored marker VALUE is the source.
 *
 * - `gpc`: the browser sent Global Privacy Control when the request was made.
 *   GPC is a live browser signal, so if it is OFF by the time the request is
 *   resumed, the unconfirmed request is stale and may be dropped. Nothing is
 *   opted in: an opt-out the server already saved stays saved (it is sticky and
 *   support-clearable only).
 * - `explicit`: the person asked for the opt-out themselves. NEVER dropped
 *   because a browser signal changed; it is retried until acknowledged.
 *
 * The legacy value `'true'` (this key's first format, which only the GPC path
 * ever wrote) reads as `gpc`. Keep public/oaiq-loader.js's value list in step.
 */
export type OptOutSource = 'gpc' | 'explicit';

export function parsePendingOptOut(value: string | null): OptOutSource | null {
  if (value === 'explicit') return 'explicit';
  if (value === 'gpc' || value === 'true') return 'gpc';
  return null;
}

type PrivacyWindow = Window & {
  __jdsAdsConsentDenied?: boolean;
  oaiq?: (command: string, value: boolean) => void;
};

export function denyBrowserMeasurement(): void {
  if (typeof window === 'undefined') return;
  const browser = window as PrivacyWindow;
  browser.__jdsAdsConsentDenied = true;
  try { browser.oaiq?.('consent', false); } catch { /* SDK errors must not block saving the account preference */ }
}

export function hasPendingBrowserOptOut(): boolean {
  if (typeof window === 'undefined') return false;
  if ((window as PrivacyWindow).__jdsAdsConsentDenied) return true;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(PENDING_OPT_OUT_PREFIX) && parsePendingOptOut(localStorage.getItem(key)) !== null) return true;
    }
  } catch { /* the in-page flag still protects requests when storage is denied */ }
  return false;
}
